import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Percent, LineChart, RefreshCw, Save, RotateCcw, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { settingsAPI, EconomicIndicators as EconomicIndicatorsType, CalculatorDefaults } from '../lib/settings-api';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';

export function EconomicIndicators() {
  const [indicators, setIndicators] = useState<EconomicIndicatorsType | null>(null);
  const [calculatorDefaults, setCalculatorDefaults] = useState<CalculatorDefaults | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [updatedBy, setUpdatedBy] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const { settings } = await settingsAPI.getSettings();
      setIndicators(settings.economicIndicators);
      setCalculatorDefaults(settings.calculatorDefaults);
      setLastUpdated(settings.lastUpdated);
      setUpdatedBy(settings.updatedBy);
    } catch (error) {
      logger.error('Failed to load settings', error as Error, undefined, 'EconomicIndicators');
      toast.error('Failed to load economic indicators');
    } finally {
      setIsLoading(false);
    }
  };

  const handleIndicatorChange = (field: keyof EconomicIndicatorsType, value: string) => {
    if (!indicators) return;
    
    const numValue = parseFloat(value);
    
    // Validation
    if (isNaN(numValue)) {
      toast.error(`Invalid value for ${field}`);
      return;
    }
    
    // Range validation
    if (field === 'interestRate' && (numValue < 0 || numValue > 30)) {
      toast.error('Interest rate must be between 0% and 30%');
      return;
    }
    if (field === 'inflationRate' && (numValue < -10 || numValue > 50)) {
      toast.error('Inflation rate must be between -10% and 50%');
      return;
    }
    if (field === 'exchangeRateUSD' && numValue < 0) {
      toast.error('Exchange rate must be positive');
      return;
    }
    
    setIndicators({
      ...indicators,
      [field]: numValue
    });
    setHasChanges(true);
  };

  const handleDefaultChange = (field: keyof CalculatorDefaults, value: string) => {
    if (!calculatorDefaults) return;
    
    const numValue = parseFloat(value);
    
    // Validation
    if (isNaN(numValue)) {
      toast.error(`Invalid value for ${field}`);
      return;
    }
    
    // Range validation
    if (field === 'loanTerm' && (numValue < 1 || numValue > 40)) {
      toast.error('Loan term must be between 1 and 40 years');
      return;
    }
    if (field === 'depositPercentage' && (numValue < 0 || numValue > 100)) {
      toast.error('Deposit percentage must be between 0% and 100%');
      return;
    }
    if (field === 'vatRate' && (numValue < 0 || numValue > 1)) {
      toast.error('VAT rate must be between 0 and 1 (e.g. 0.155 for 15.5%)');
      return;
    }
    if ((field === 'bankInitFee' || field === 'bondDeedsFee' || field === 'transferDeedsFee' || field === 'postFicaFee') && numValue < 0) {
      toast.error('Fee values must be positive');
      return;
    }
    if (field === 'transferDutyRate' && (numValue < 0 || numValue > 20)) {
      toast.error('Transfer duty rate must be between 0% and 20%');
      return;
    }
    
    setCalculatorDefaults({
      ...calculatorDefaults,
      [field]: numValue
    });
    setHasChanges(true);
  };

  const validateAllSettings = (): boolean => {
    if (!indicators || !calculatorDefaults) return false;
    
    // Check for negative or zero critical values
    if (indicators.interestRate <= 0) {
      toast.error('Interest rate must be greater than 0');
      return false;
    }
    
    if (calculatorDefaults.loanTerm <= 0) {
      toast.error('Loan term must be greater than 0');
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!indicators || !calculatorDefaults) return;
    
    // Validate before saving
    if (!validateAllSettings()) {
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Update both economic indicators and calculator defaults
      await settingsAPI.updateEconomicIndicators(indicators, 'admin');
      await settingsAPI.updateCalculatorDefaults(calculatorDefaults, 'admin');
      
      toast.success('Settings saved successfully! All calculators will use the updated values.');
      setHasChanges(false);
      await loadSettings();
    } catch (error) {
      logger.error('Failed to save settings', error as Error, undefined, 'EconomicIndicators');
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsSaving(true);
      await settingsAPI.resetSettings('admin');
      toast.success('Settings reset to defaults');
      setHasChanges(false);
      await loadSettings();
    } catch (error) {
      logger.error('Failed to reset settings', error as Error, undefined, 'EconomicIndicators');
      toast.error('Failed to reset settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = () => {
    setHasChanges(false);
    loadSettings();
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading economic indicators...</p>
        </CardContent>
      </Card>
    );
  }

  if (!indicators || !calculatorDefaults) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load economic indicators</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <LineChart className="w-5 h-5 text-primary" />
            Economic Indicators & Calculator Defaults
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Manage key economic rates and default values for property calculators
          </p>
          {lastUpdated && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              Last updated: {new Date(lastUpdated).toLocaleString()} by {updatedBy}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isSaving}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={isSaving || !hasChanges}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <Alert>
          <AlertDescription>
            You have unsaved changes. Click "Save Changes" to apply them.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Economic Indicators */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Economic Indicators
            </CardTitle>
            <CardDescription>
              Current South African economic indicators
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="interestRate" className="flex items-center gap-2">
                <Percent className="w-3 h-3" />
                Prime Interest Rate (%)
              </Label>
              <Input
                id="interestRate"
                type="number"
                step="0.01"
                value={indicators.interestRate}
                onChange={(e) => handleIndicatorChange('interestRate', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Used in affordability and bond calculators</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="repoRate">SARB Repo Rate (%)</Label>
              <Input
                id="repoRate"
                type="number"
                step="0.01"
                value={indicators.repoRate}
                onChange={(e) => handleIndicatorChange('repoRate', e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inflationRate">Inflation Rate (CPI %)</Label>
              <Input
                id="inflationRate"
                type="number"
                step="0.01"
                value={indicators.inflationRate}
                onChange={(e) => handleIndicatorChange('inflationRate', e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gdpGrowth">GDP Growth Rate (%)</Label>
              <Input
                id="gdpGrowth"
                type="number"
                step="0.01"
                value={indicators.gdpGrowth}
                onChange={(e) => handleIndicatorChange('gdpGrowth', e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unemploymentRate">Unemployment Rate (%)</Label>
              <Input
                id="unemploymentRate"
                type="number"
                step="0.01"
                value={indicators.unemploymentRate}
                onChange={(e) => handleIndicatorChange('unemploymentRate', e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exchangeRateUSD" className="flex items-center gap-2">
                <DollarSign className="w-3 h-3" />
                Exchange Rate (ZAR to USD)
              </Label>
              <Input
                id="exchangeRateUSD"
                type="number"
                step="0.01"
                value={indicators.exchangeRateUSD}
                onChange={(e) => handleIndicatorChange('exchangeRateUSD', e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="propertyGrowthRate">Property Price Growth Rate (%/year)</Label>
              <Input
                id="propertyGrowthRate"
                type="number"
                step="0.01"
                value={indicators.propertyGrowthRate}
                onChange={(e) => handleIndicatorChange('propertyGrowthRate', e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rentalYield">Average Rental Yield (%)</Label>
              <Input
                id="rentalYield"
                type="number"
                step="0.01"
                value={indicators.rentalYield}
                onChange={(e) => handleIndicatorChange('rentalYield', e.target.value)}
                className="font-mono"
              />
            </div>
          </CardContent>
        </Card>

        {/* Calculator Defaults */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LineChart className="w-4 h-4 text-primary" />
              Calculator Default Values
            </CardTitle>
            <CardDescription>
              Default values for property and bond calculators
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loanTerm">Default Loan Term (years)</Label>
              <Input
                id="loanTerm"
                type="number"
                value={calculatorDefaults.loanTerm}
                onChange={(e) => handleDefaultChange('loanTerm', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Typical home loan period</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="depositPercentage">Default Deposit (%)</Label>
              <Input
                id="depositPercentage"
                type="number"
                step="1"
                value={calculatorDefaults.depositPercentage}
                onChange={(e) => handleDefaultChange('depositPercentage', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Minimum deposit percentage</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transferDutyRate">Transfer Duty Rate (%)</Label>
              <Input
                id="transferDutyRate"
                type="number"
                step="1"
                value={calculatorDefaults.transferDutyRate}
                onChange={(e) => handleDefaultChange('transferDutyRate', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Maximum transfer duty rate for high-value properties</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bondRegistrationFee">Bond Registration Fee (ZAR)</Label>
              <Input
                id="bondRegistrationFee"
                type="number"
                value={calculatorDefaults.bondRegistrationFee}
                onChange={(e) => handleDefaultChange('bondRegistrationFee', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Fixed fee for bond registration</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deedsOfficeFee">Deeds Office Fee (ZAR)</Label>
              <Input
                id="deedsOfficeFee"
                type="number"
                value={calculatorDefaults.deedsOfficeFee}
                onChange={(e) => handleDefaultChange('deedsOfficeFee', e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">Fixed fee for deeds office registration</p>
            </div>

            {/* Transfer Cost Calculator Settings */}
            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="text-sm font-semibold mb-4">Transfer Cost Calculator</h4>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vatRate">VAT Rate (decimal)</Label>
                  <Input
                    id="vatRate"
                    type="number"
                    step="0.001"
                    value={calculatorDefaults.vatRate ?? 0.155}
                    onChange={(e) => handleDefaultChange('vatRate', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">VAT rate as decimal — 0.155 = 15.5%. Applied to attorney fees.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bankInitFee">Bank Initiation Fee (ZAR incl. VAT)</Label>
                  <Input
                    id="bankInitFee"
                    type="number"
                    value={calculatorDefaults.bankInitFee ?? 6064}
                    onChange={(e) => handleDefaultChange('bankInitFee', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">NCA-regulated cap, inclusive of VAT</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bondDeedsFee">Bond Deeds Office Fee (ZAR)</Label>
                  <Input
                    id="bondDeedsFee"
                    type="number"
                    value={calculatorDefaults.bondDeedsFee ?? 2767}
                    onChange={(e) => handleDefaultChange('bondDeedsFee', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">Fixed deeds office fee for bond registration (v1.0)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="transferDeedsFee">Transfer Deeds Office Fee (ZAR)</Label>
                  <Input
                    id="transferDeedsFee"
                    type="number"
                    value={calculatorDefaults.transferDeedsFee ?? 3296}
                    onChange={(e) => handleDefaultChange('transferDeedsFee', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">Fixed deeds office fee for property transfer (v1.0)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postFicaFee">Post, Petties, FICA Fee (ZAR incl. VAT)</Label>
                  <Input
                    id="postFicaFee"
                    type="number"
                    value={calculatorDefaults.postFicaFee ?? 2496}
                    onChange={(e) => handleDefaultChange('postFicaFee', e.target.value)}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">Applied to both bond and transfer sides</p>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Calculator Integration
              </h4>
              <p className="text-xs text-muted-foreground">
                These values are automatically used by the Affordability Calculator, Bond Calculator,
                and Transfer Cost Calculator throughout the platform. Changes take effect immediately
                for all users.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}